import {
	AddIcon,
	ArrowDownIcon,
	ArrowUpIcon,
	Button,
	DeleteIcon,
	Input,
	Modal,
	Select,
	Text,
} from '@viapeople/components'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useToast } from '../utils/ToastContext'
import { useEvaluationHeaderStore } from './stores/evaluationHeaderStore'

interface EvaluationHeaderModalProps {
	isOpen: boolean
	onClose: () => void
	organizationId: string
	formId?: string | null
}

const PERSONA_OPTIONS = [
	{ value: '__default__', label: 'Default (All Roles)' },
	{ value: 'participant', label: 'Self Review' },
	{ value: 'peer', label: 'Peer Review' },
	{ value: 'direct_reports', label: 'Direct Reports Review' },
	{ value: 'primary_manager', label: 'Manager Review' },
	{ value: 'high_level_manager', label: 'High Level Manager Review' },
	{ value: 'secondary_manager', label: 'Secondary Manager Review' },
	{ value: 'co_manager', label: 'Manager Review (Co-Manager)' },
	{ value: 'up', label: 'Upward Review' },
	{ value: 'down', label: 'Downward Review' },
	{ value: 'rater', label: 'Rater' },
	{ value: 'unsolicited', label: 'Unsolicited Feedback' },
	{ value: 'cycle_admin', label: 'Cycle Admin' },
]

type FieldType = 'customField' | 'hierarchy' | 'user' | 'system' | 'evaluator' | 'computed'

/**
 * Shared Evaluation Header Configuration Modal
 *
 * Used for both:
 * - Organization-level default configuration (formId = null/undefined)
 * - Form-specific configuration (formId = valid form UUID)
 */
export const EvaluationHeaderModal: React.FC<EvaluationHeaderModalProps> = ({
	isOpen,
	onClose,
	organizationId,
	formId = null,
}) => {
	const toast = useToast()

	// Store state and actions
	const {
		fields,
		availableFields,
		isLoading,
		isSaving,
		error,
		initialize,
		reset,
		saveConfig,
		copyFromOrgDefaults,
		addField,
		removeField,
		updateFieldName,
		moveFieldUp,
		moveFieldDown,
		selectedPersona,
		setSelectedPersona,
	} = useEvaluationHeaderStore()

	// Local UI state for field creation
	const [newFieldType, setNewFieldType] = useState<FieldType | ''>('customField')
	const [selectedCustomField, setSelectedCustomField] = useState<string>('')
	const [selectedHierarchySource, setSelectedHierarchySource] = useState<string>('')
	const [selectedUserField, setSelectedUserField] = useState<string>('')
	const [selectedSystemField, setSelectedSystemField] = useState<string>('')
	const [selectedEvaluatorField, setSelectedEvaluatorField] = useState<string>('')
	const [newFieldName, setNewFieldName] = useState<string>('')
	const [isComputedBuilderOpen, setIsComputedBuilderOpen] = useState(false)
	const [computedSources, setComputedSources] = useState<
		Array<{ type: 'customFields' | 'hierarchy' | 'user' | 'system' | 'evaluator'; key: string }>
	>([])
	const [computedConnector, setComputedConnector] = useState<string>(' ')

	// Derived options from available fields
	const hierarchyOptions =
		availableFields?.hierarchyFields.map((h) => ({
			value: h.key,
			label: h.label,
			displayLabel: h.label,
		})) || []

	const userFieldOptions =
		availableFields?.userFields.map((u) => ({
			value: u.key,
			label: u.label,
		})) || []

	const systemOptions =
		availableFields?.systemFields.map((s) => ({
			value: s.key,
			label: s.label,
		})) || []

	const evaluatorFieldOptions =
		availableFields?.evaluatorFields?.map((e) => ({
			value: e.key,
			label: e.label,
		})) || []

	const customFields = availableFields?.customFields || []

	// Determine if form is using org defaults (only relevant when editing form-specific config)
	const isUsingOrgDefault = useMemo(() => {
		if (!formId) return false // Not applicable for org-level config
		// Form requests use skipFallback=true, so empty means inheriting from org defaults.
		return fields.length === 0
	}, [formId, fields])

	// Initialize store when modal opens
	useEffect(() => {
		if (isOpen && organizationId) {
			initialize(organizationId, formId, null)
			// Reset local UI state
			setSelectedCustomField('')
			setSelectedHierarchySource('')
			setSelectedUserField('')
			setNewFieldType('customField')
			setNewFieldName('')
			setSelectedSystemField('')
			setSelectedEvaluatorField('')
			setIsComputedBuilderOpen(false)
			setComputedSources([])
			setComputedConnector(' ')
		}
	}, [isOpen, organizationId, formId, initialize])

	// Reset store when modal closes
	useEffect(() => {
		if (!isOpen) {
			reset()
		}
	}, [isOpen, reset])

	// Show error toasts
	useEffect(() => {
		if (error) {
			toast.showToast(error, 'error')
		}
	}, [error, toast])

	const handleAddField = () => {
		if (newFieldType === 'computed') {
			setIsComputedBuilderOpen(true)
			setNewFieldType('')
			return
		}

		let sources: Array<{ type: 'customFields' | 'hierarchy' | 'user' | 'system' | 'evaluator'; key: string }> = []
		let defaultName = ''

		if (newFieldType === 'customField' && selectedCustomField) {
			sources = [{ type: 'customFields', key: selectedCustomField }]
			defaultName = selectedCustomField
		} else if (newFieldType === 'hierarchy' && selectedHierarchySource) {
			sources = [{ type: 'hierarchy', key: selectedHierarchySource }]
			const hierarchyField = hierarchyOptions.find((h) => h.value === selectedHierarchySource)
			defaultName = hierarchyField?.label || selectedHierarchySource
		} else if (newFieldType === 'user' && selectedUserField) {
			sources = [{ type: 'user', key: selectedUserField }]
			const userField = userFieldOptions.find((u) => u.value === selectedUserField)
			defaultName = userField?.label || selectedUserField
		} else if (newFieldType === 'system' && selectedSystemField) {
			sources = [{ type: 'system', key: selectedSystemField }]
			const systemField = systemOptions.find((s) => s.value === selectedSystemField)
			defaultName = systemField?.label || selectedSystemField
		} else if (newFieldType === 'evaluator' && selectedEvaluatorField) {
			sources = [{ type: 'evaluator', key: selectedEvaluatorField }]
			const evaluatorField = evaluatorFieldOptions.find((e) => e.value === selectedEvaluatorField)
			defaultName = evaluatorField?.label || selectedEvaluatorField
		}

		if (sources.length > 0) {
			addField({
				name: newFieldName || defaultName,
				sources,
				formId,
			})
			setSelectedCustomField('')
			setSelectedHierarchySource('')
			setSelectedUserField('')
			setSelectedSystemField('')
			setSelectedEvaluatorField('')
			setNewFieldName('')
		}
	}

	const handleAddComputedField = () => {
		if (computedSources.length < 2) {
			toast.showToast('Computed fields require at least 2 sources', 'error')
			return
		}

		const defaultName =
			computedSources
				.map((source) => {
					if (source.type === 'hierarchy') {
						return hierarchyOptions.find((h) => h.value === source.key)?.label || source.key
					}
					if (source.type === 'user') {
						return userFieldOptions.find((u) => u.value === source.key)?.label || source.key
					}
					if (source.type === 'system') {
						return systemOptions.find((s) => s.value === source.key)?.label || source.key
					}
					if (source.type === 'evaluator') {
						return evaluatorFieldOptions.find((e) => e.value === source.key)?.label || source.key
					}
					return source.key
				})
				.join(' ') || 'Computed Field'

		addField({
			name: newFieldName || defaultName,
			sources: computedSources,
			connector: computedConnector,
			formId,
		})

		setIsComputedBuilderOpen(false)
		setComputedSources([])
		setComputedConnector(' ')
		setNewFieldName('')
		setNewFieldType('customField')
	}

	const handleSave = async () => {
		try {
			await saveConfig()
			const target = formId
				? personaLabel
					? `${personaLabel} evaluation header`
					: 'Form evaluation header'
				: 'Organization default evaluation header'
			toast.showToast(`${target} configuration saved successfully`)
			onClose()
		} catch {
			// Error is already handled by the store
		}
	}

	const addSourceToComputedField = () => {
		let source: { type: 'customFields' | 'hierarchy' | 'user' | 'system' | 'evaluator'; key: string } | null = null

		if (newFieldType === 'customField' && selectedCustomField) {
			source = { type: 'customFields', key: selectedCustomField }
		} else if (newFieldType === 'hierarchy' && selectedHierarchySource) {
			source = { type: 'hierarchy', key: selectedHierarchySource }
		} else if (newFieldType === 'user' && selectedUserField) {
			source = { type: 'user', key: selectedUserField }
		} else if (newFieldType === 'system' && selectedSystemField) {
			source = { type: 'system', key: selectedSystemField }
		} else if (newFieldType === 'evaluator' && selectedEvaluatorField) {
			source = { type: 'evaluator', key: selectedEvaluatorField }
		}

		if (source) {
			setComputedSources([...computedSources, source])
			setSelectedCustomField('')
			setSelectedHierarchySource('')
			setSelectedUserField('')
			setNewFieldType('')
			setSelectedSystemField('')
			setSelectedEvaluatorField('')
		}
	}

	const removeSourceFromComputedField = (index: number) => {
		setComputedSources(computedSources.filter((_, i) => i !== index))
	}

	const renderSourceLabel = (source: { type: string; key: string }) => {
		if (source.type === 'hierarchy') {
			const field = hierarchyOptions.find((h) => h.value === source.key)
			return field?.label || source.key
		}
		if (source.type === 'user') {
			const field = userFieldOptions.find((u) => u.value === source.key)
			return field?.label || source.key
		}
		if (source.type === 'evaluator') {
			const field = evaluatorFieldOptions.find((e) => e.value === source.key)
			return field?.label || source.key
		}
		return source.key
	}

	const personaLabel = selectedPersona
		? (PERSONA_OPTIONS.find((o) => o.value === selectedPersona)?.label ?? selectedPersona)
		: null

	const modalTitle = formId
		? `Configure Evaluation Header${personaLabel ? ` — ${personaLabel}` : ''}`
		: 'Configure Organization Default Evaluation Header'

	const helpText = formId
		? 'Configure the evaluation header (blue box) for this form. Use the role selector to set up different headers per persona. If no role-specific header exists, the default form header is shown.'
		: 'Configure the default evaluation header fields for all forms in this organization unless overridden at the form level.'

	if (isLoading) {
		return (
			<Modal isOpen={isOpen} onClose={onClose} title={modalTitle} className='w-160'>
				<div className='flex items-center justify-center p-8'>
					<Text>Loading...</Text>
				</div>
			</Modal>
		)
	}

	return (
		<Modal isOpen={isOpen} onClose={onClose} title={modalTitle} className='w-160'>
			<div className='flex flex-col gap-4'>
				<Text variant='small' className='text-neutral-600'>
					{helpText}
				</Text>

				{/* Persona selector — only for form-level config */}
				{formId && (
					<div className='flex flex-col gap-3 p-3 border rounded-lg bg-neutral-50'>
						<div className='flex flex-col gap-1'>
							<Text variant='small' weight='semibold'>
								Configure for Role
							</Text>
							<Text variant='xsmall' className='text-neutral-500'>
								Select a role to configure a separate header, or keep "Default" to set the fallback header for all
								roles.
							</Text>
						</div>
						<Select
							value={selectedPersona ?? '__default__'}
							onChange={(value) => {
								void setSelectedPersona(value === '__default__' ? null : value)
							}}
							options={PERSONA_OPTIONS}
						/>
						{isUsingOrgDefault && (
							<div className='flex items-center justify-between gap-2 p-2 rounded bg-blue-50 border border-blue-200'>
								<Text variant='xsmall' className='text-blue-800'>
									{selectedPersona
										? 'No role-specific header configured. This role will inherit the form default. Save fields below to create a custom header for this role.'
										: 'No form-level header configured. This form inherits the organization default. Save fields below to override.'}
								</Text>
								<Button variant='secondary' size='small' onClick={copyFromOrgDefaults} disabled={isLoading}>
									Copy from Org Defaults
								</Button>
							</div>
						)}
						{!isUsingOrgDefault && selectedPersona && (
							<div className='p-2 rounded bg-green-50 border border-green-200'>
								<Text variant='xsmall' className='text-green-800'>
									Custom header active for this role. Save to keep or remove all fields to revert to the form default.
								</Text>
							</div>
						)}
					</div>
				)}

				{/* Current Fields */}
				<div className='flex flex-col gap-2'>
					<Text variant='small' weight='semibold'>
						Current Fields
					</Text>
					{fields.length === 0 ? (
						<Text variant='small' className='text-neutral-500'>
							No fields configured. Add fields below.
						</Text>
					) : (
						fields.map((field, index) => (
							<div key={field.id} className='flex items-center gap-2 p-3 border rounded-lg'>
								<div className='flex-1 flex flex-col gap-1'>
									<Input
										value={field.name}
										onChange={(e) => updateFieldName(field.id as string, e.target.value)}
										placeholder='Field name'
									/>
									<Text variant='xsmall' className='text-neutral-500'>
										{field.sources.length === 1
											? `Source: ${renderSourceLabel(field.sources[0])}`
											: `Computed from: ${field.sources.map(renderSourceLabel).join(' + ')}`}
									</Text>
								</div>
								<div className='flex gap-1'>
									<Button variant='tertiary' size='small' onClick={() => moveFieldUp(index)} disabled={index === 0}>
										<ArrowUpIcon />
									</Button>
									<Button
										variant='tertiary'
										size='small'
										onClick={() => moveFieldDown(index)}
										disabled={index === fields.length - 1}>
										<ArrowDownIcon />
									</Button>
									<Button variant='tertiary' size='small' onClick={() => removeField(field.id as string)}>
										<DeleteIcon />
									</Button>
								</div>
							</div>
						))
					)}
				</div>

				{/* Add New Field */}
				{!isComputedBuilderOpen ? (
					<div className='flex flex-col gap-3 p-4 border rounded-lg bg-neutral-50'>
						<Text variant='small' weight='semibold'>
							Add New Field
						</Text>
						<div className='flex gap-2'>
							<Select
								value={newFieldType}
								onChange={(value) => setNewFieldType(value as FieldType)}
								options={[
									{ value: 'customField', label: 'Custom Field' },
									{ value: 'hierarchy', label: 'Hierarchy Field' },
									{ value: 'system', label: 'System Field' },
									{ value: 'user', label: 'User Field' },
									{ value: 'evaluator', label: 'Reviewer Field' },
									{ value: 'computed', label: 'Computed Field' },
								]}
							/>
							{newFieldType === 'customField' && (
								<Select
									value={selectedCustomField}
									onChange={setSelectedCustomField}
									options={customFields.map((f) => ({ value: f.key, label: f.key }))}
									placeholder='Select custom field'
								/>
							)}
							{newFieldType === 'hierarchy' && (
								<Select
									value={selectedHierarchySource}
									onChange={setSelectedHierarchySource}
									options={hierarchyOptions}
									placeholder='Select hierarchy field'
								/>
							)}
							{newFieldType === 'user' && (
								<Select
									value={selectedUserField}
									onChange={setSelectedUserField}
									options={userFieldOptions}
									placeholder='Select user field'
								/>
							)}
							{newFieldType === 'system' && (
								<Select
									value={selectedSystemField}
									onChange={setSelectedSystemField}
									options={systemOptions}
									placeholder='Select system field'
								/>
							)}
							{newFieldType === 'evaluator' && (
								<Select
									value={selectedEvaluatorField}
									onChange={setSelectedEvaluatorField}
									options={evaluatorFieldOptions}
									placeholder='Select reviewer field'
								/>
							)}
						</div>
						<Input
							value={newFieldName}
							onChange={(e) => setNewFieldName(e.target.value)}
							placeholder='Custom field name (optional)'
						/>
						<Button variant='secondary' onClick={handleAddField} size='small' icon={AddIcon} iconPosition='left'>
							Add Field
						</Button>
					</div>
				) : (
					<div className='flex flex-col gap-3 p-4 border rounded-lg bg-neutral-50'>
						<div className='flex items-center justify-between'>
							<Text variant='small' weight='semibold'>
								Build Computed Field
							</Text>
							<Button
								variant='tertiary'
								size='small'
								onClick={() => {
									setIsComputedBuilderOpen(false)
									setComputedSources([])
									setComputedConnector(' ')
									setNewFieldName('')
									setNewFieldType('customField')
								}}>
								Cancel
							</Button>
						</div>
						<Text variant='xsmall' className='text-neutral-500'>
							Add 2 or more sources to create a computed field. Values will be joined using the connector below.
						</Text>
						<div className='flex items-center gap-2'>
							<Text variant='xsmall' className='text-neutral-500 shrink-0'>
								Connector:
							</Text>
							<Select
								value={computedConnector}
								onChange={(value) => setComputedConnector(value as string)}
								options={[
									{ value: ' ', label: 'Space ( )' },
									{ value: ', ', label: 'Comma (, )' },
									{ value: ' - ', label: 'Dash ( - )' },
									{ value: ' | ', label: 'Pipe ( | )' },
									{ value: '', label: 'None (concatenate)' },
								]}
							/>
						</div>
						<div className='flex flex-col gap-2'>
							{computedSources.map((source, index) => (
								<div
									key={`${source.type}-${source.key}`}
									className='flex items-center gap-2 p-2 border rounded bg-white'>
									<Text variant='small' className='flex-1'>
										{renderSourceLabel(source)}
									</Text>
									<Button variant='tertiary' size='small' onClick={() => removeSourceFromComputedField(index)}>
										<DeleteIcon />
									</Button>
								</div>
							))}
						</div>
						<div className='flex flex-col gap-2'>
							<Select
								value={newFieldType}
								onChange={(value) => setNewFieldType(value as FieldType)}
								options={[
									{ value: 'customField', label: 'Custom Field' },
									{ value: 'hierarchy', label: 'Hierarchy Field' },
									{ value: 'system', label: 'System Field' },
									{ value: 'user', label: 'User Field' },
									{ value: 'evaluator', label: 'Reviewer Field' },
								]}
								placeholder='Select a data source'
							/>
							{newFieldType === 'customField' && (
								<Select
									value={selectedCustomField}
									onChange={setSelectedCustomField}
									options={customFields.map((f) => ({ value: f.key, label: f.key }))}
									placeholder='Select custom field'
								/>
							)}
							{newFieldType === 'hierarchy' && (
								<Select
									value={selectedHierarchySource}
									onChange={setSelectedHierarchySource}
									options={hierarchyOptions}
									placeholder='Select hierarchy field'
								/>
							)}
							{newFieldType === 'user' && (
								<Select
									value={selectedUserField}
									onChange={setSelectedUserField}
									options={userFieldOptions}
									placeholder='Select user field'
								/>
							)}
							{newFieldType === 'system' && (
								<Select
									value={selectedSystemField}
									onChange={setSelectedSystemField}
									options={systemOptions}
									placeholder='Select system field'
								/>
							)}
							{newFieldType === 'evaluator' && (
								<Select
									value={selectedEvaluatorField}
									onChange={setSelectedEvaluatorField}
									options={evaluatorFieldOptions}
									placeholder='Select reviewer field'
								/>
							)}
							<Button
								variant='secondary'
								onClick={addSourceToComputedField}
								size='small'
								disabled={
									!newFieldType ||
									newFieldType === 'computed' ||
									(newFieldType === 'customField' && !selectedCustomField) ||
									(newFieldType === 'hierarchy' && !selectedHierarchySource) ||
									(newFieldType === 'user' && !selectedUserField) ||
									(newFieldType === 'system' && !selectedSystemField) ||
									(newFieldType === 'evaluator' && !selectedEvaluatorField)
								}>
								Add Source
							</Button>
						</div>
						<Input
							value={newFieldName}
							onChange={(e) => setNewFieldName(e.target.value)}
							placeholder='Computed field name (optional)'
						/>
						<Button
							variant='primary'
							onClick={handleAddComputedField}
							size='small'
							disabled={computedSources.length < 2}>
							Create Computed Field
						</Button>
					</div>
				)}

				{/* Action Buttons */}
				<div className='flex justify-end gap-2 mt-4'>
					<Button variant='secondary' onClick={onClose}>
						Cancel
					</Button>
					<Button variant='primary' onClick={handleSave} disabled={isSaving}>
						{isSaving ? 'Saving...' : 'Save Configuration'}
					</Button>
				</div>
			</div>
		</Modal>
	)
}
